/**
 * TURN CC · 23 — Upcoming Corporate Events strip.
 *
 * Horizontally-scrollable strip of gradient cards shown above the
 * calendar, showcasing every upcoming Beyond Activ conference in the
 * next ~year. Click a card → same side panel as the calendar.
 *
 * Data: `useCalendarEvents` (types=corporate_event) with a wide future
 * window. Kept purely a UI concern — the data endpoint is unchanged.
 */
import { useMemo } from "react";
import { MapPin } from "lucide-react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useCalendarEvents } from "@/hooks/useCalendarEvents";

const FUTURE_WINDOW_DAYS = 500;

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function shortDateRange(startIso, endIso) {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN",
                  "JUL","AUG","SEP","OCT","NOV","DEC"];
  const sd = s.getUTCDate();
  const ed = e.getUTCDate();
  const sm = MONTHS[s.getUTCMonth()];
  const em = MONTHS[e.getUTCMonth()];
  const sy = s.getUTCFullYear();
  const ey = e.getUTCFullYear();
  if (sy === ey && sm === em) return `${sd}-${ed} ${sm} ${sy}`;
  if (sy === ey)               return `${sd} ${sm} → ${ed} ${em} ${sy}`;
  return `${sd} ${sm} ${sy} → ${ed} ${em} ${ey}`;
}

function daysUntil(iso) {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.round((then - now) / 86400000);
  return diff;
}

function countdownLabel(iso) {
  const d = daysUntil(iso);
  if (d < 0)  return `${Math.abs(d)}d ago`;
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  if (d < 30) return `in ${d}d`;
  if (d < 365) {
    const m = Math.round(d / 30);
    return `in ${m}mo`;
  }
  return `in ${Math.round(d / 365)}y`;
}

export default function UpcomingEventsStrip({ onEventClick }) {
  const today = useMemo(() => new Date(), []);
  const from = ymd(today);
  const to = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + FUTURE_WINDOW_DAYS);
    return ymd(d);
  }, [today]);

  const { data, isPending } = useCalendarEvents({
    from, to, types: ["corporate_event"],
  });

  const upcoming = useMemo(() => {
    const evts = (data?.events || []).filter(
      (e) => e.type === "corporate_event"
          && new Date(e.ends_at) >= today,
    );
    evts.sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || ""));
    return evts;
  }, [data, today]);

  // First-ever visit (no cache) → render a discreet skeleton row so the
  // module never blank-flashes. Once we've had at least one successful
  // response, an empty result means "no upcoming events" and we hide.
  if (isPending && !data) {
    return (
      <div
        data-testid="upcoming-events-strip-skeleton"
        className="flex flex-col gap-2"
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">
          Upcoming Corporate Events
        </div>
        <div className="flex gap-3 pb-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="shrink-0 min-w-[220px] h-24 rounded-xl bg-slate-100 dark:bg-white/[0.03] animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (upcoming.length === 0) return null;

  return (
    <div
      data-testid="upcoming-events-strip"
      className="flex flex-col gap-2"
    >
      <div className="flex items-baseline gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
          Upcoming Corporate Events
        </span>
        <span className="text-[11px] font-semibold text-slate-400 dark:text-slate-500">
          · {upcoming.length}
        </span>
      </div>
      <ScrollArea className="w-full">
        <div className="flex gap-3 pb-2">
          {upcoming.map((evt) => (
            <button
              key={evt.id}
              type="button"
              data-testid={`upcoming-event-${evt.id}`}
              onClick={() => onEventClick?.(evt)}
              className={
                "group relative shrink-0 min-w-[220px] max-w-[240px] " +
                "p-3.5 text-left text-white rounded-xl shadow-sm " +
                "bg-gradient-to-br from-red-500 to-rose-600 " +
                "hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 " +
                "focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-2 " +
                "dark:focus:ring-offset-slate-950"
              }
            >
              <span
                data-testid={`upcoming-event-countdown-${evt.id}`}
                className="absolute top-2 right-2 text-[10px] font-bold bg-white/20 backdrop-blur-sm px-1.5 py-0.5 rounded-full"
              >
                {countdownLabel(evt.starts_at)}
              </span>
              <div className="text-[10px] font-semibold uppercase tracking-widest opacity-80 pr-14">
                {shortDateRange(evt.starts_at, evt.ends_at)}
              </div>
              <div className="mt-1.5 text-sm font-semibold leading-tight">
                {evt.title}
              </div>
              {evt.location && (
                <div className="mt-1 text-xs opacity-90 flex items-center gap-1">
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span className="truncate">{evt.location}</span>
                </div>
              )}
            </button>
          ))}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}
